import {
  AppstoreAddOutlined,
  AppstoreFilled,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  IdcardOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AccordionItem, Link, Tooltip } from '@mm/components';
import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import {
  hiddenVariables,
  normalizeVariableName,
  variablesByCategory,
} from '@mm/state/helpers/variable-names-helper';
import {
  ContactVariable,
  SelectedContact,
} from '@mm/state/interfaces/contacts';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectBusinessAttributes } from '@mm/state/selectors/attributeSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import { camelCase, isNil, uniqueId } from 'lodash';
import moment from 'moment';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import { ReactComponent as ClickedIcon } from 'assets/click.svg';
import { SearchInput } from '../../../../../../../common/components/SearchInput/SearchInput';
import {
  INodeMenuItem,
  Menu,
} from '../../../../../beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { IntegrationIcon } from '../../../../../beta/modules/Integrations/IntegrationIcon/IntegrationIcon';
import './ChatVariablesTab.pcss';
import { BusinessAttributes } from '../../../../../liveChat/components/Contact/components/BusinessAttributes';

type Props = {
  isLiveChatContacts?: boolean;
  variables: ContactVariable[];
  contactId?: number;
  selectedContact?: SelectedContact;
  onAttributeUpdated?: (busyComponent?: string) => void;
  remoteUserId: string;
  collapsedContactView?: boolean;
  withHeaderButton?: boolean;
  withHeaderButtonText?: string;
  onHeaderButtonClick?: any;
  openArrowClassName?: string;
  headerButtonCss?: {
    container?: string;
    leftIcon?: string;
  };
  isBusinessContact?: boolean;
};

const EDITABLE_SYSTEM_VARS = ['first name', 'last name', 'full name'];

export const processAttributes = (
  attr: any,
  variableName: string,
): ReactNode => {
  const timeFormats = [
    'YYYY-MM-DD HH:mm:ss [UTC]',
    'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  ];
  const time = moment.utc(attr, timeFormats, true);
  const isPhoneVar = variableName.toLowerCase() === 'phone';

  const integrationSyncRemappedTypes = Array.isArray(attr)
    ? attr?.map(item => {
        switch (item) {
          case 'capi':
            return 'facebook_ads_audience';
          case 'webhook':
            return 'default';
          default:
            return item;
        }
      })
    : [];

  switch (true) {
    case Array.isArray(attr) && variableName === 'integrationFirstSyncs':
      return integrationSyncRemappedTypes.map(a => (
        <IntegrationIcon
          key={a}
          integrationType={a}
          isActive
          className="contact-attributes__synced-integration-icon"
        />
      ));
    case variableName.toLowerCase() === 'time on site':
      return moment.utc(attr * 1000).format('mm:ss');
    case variableName.toLowerCase() === 'voy_gender':
      return attr === 'M' ? 'Male' : attr === 'F' ? 'Female' : 'Unknown';
    case typeof attr !== 'string':
      return <>{String(attr)}</>;

    case validator.isMobilePhone(attr) && isPhoneVar:
      return (
        <Link href={`tel:${attr}`} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case validator.isEmail(attr):
      return (
        <Link href={`mailto:${attr}`} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case validator.isURL(attr):
      return (
        <Link href={attr} capitalize={false} isBlank>
          {attr}
        </Link>
      );

    case time.isValid():
      return (
        <Tooltip title={time.utc().format('MM-DD-YYYY HH:mm:ss')}>
          <span>{time.local().fromNow()}</span>
        </Tooltip>
      );

    default:
      return <>{attr}</>;
  }
};

export const ChatVariablesTab = (props: Props) => {
  const {
    variables,
    isLiveChatContacts = false,
    onAttributeUpdated,
    contactId,
    remoteUserId,
    collapsedContactView,
    withHeaderButton,
    withHeaderButtonText,
    onHeaderButtonClick,
    openArrowClassName,
    headerButtonCss,
    isBusinessContact,
    selectedContact,
  } = props;

  const businessAttributes = useSelector(selectBusinessAttributes);
  const updatedVariables = variables.map(entity => ({
    ...entity,
    variable: {
      ...entity.variable,
      friendlyName: normalizeVariableName(
        entity.variable?.friendlyName
          ? entity.variable?.friendlyName
          : entity.variable.name,
      ),
    },
  }));

  const syncedToIntegrations = selectedContact?.integrationFirstSyncs?.map(
    v => v.name,
  );
  const adNetworks = ['google_ads_audience', 'facebook_ads_audience', 'capi'];

  const syncedToCRMs = [
    {
      id: Number(uniqueId()),
      variable: {
        id: Number(uniqueId()),
        name: 'integrationFirstSyncs',
        friendlyName: 'synced to CRM',
        system: true,
      },
      value: syncedToIntegrations?.filter(v => !adNetworks.includes(v)),
    },
    {
      id: Number(uniqueId()),
      variable: {
        id: Number(uniqueId()),
        name: 'integrationFirstSyncs',
        friendlyName: 'synced to ad network',
        system: true,
      },
      value: syncedToIntegrations?.filter(v => adNetworks.includes(v)),
    },
  ].filter(i => i.value?.length) as ContactVariable[];

  const variablesExcludingHidden = updatedVariables?.filter(
    variable =>
      !hiddenVariables.includes(variable.variable.name.toLocaleLowerCase()),
  );

  const personalAttributes = isBusinessContact
    ? variablesExcludingHidden.filter(
        ({ variable }) =>
          !businessAttributes.find(attr => attr.id === variable.id),
      )
    : variablesExcludingHidden;

  const sourceIdx = personalAttributes.findIndex(
    a => a.variable.name === 'source',
  );

  const conversionsAttrs = personalAttributes
    .filter(attr => attr.variable.friendlyName === 'signal events')
    .flatMap(attr =>
      [...new Set(attr.value.split(','))].filter(Boolean).map((v: string) => ({
        id: Number(uniqueId()),
        variable: {
          id: Number(uniqueId()),
          name: v,
          friendlyName: normalizeVariableName(v),
          system: true,
        },
        value: 'Yes',
      })),
    );
  const personalAttributesWithCrms = [
    ...personalAttributes.slice(0, sourceIdx),
    ...syncedToCRMs,
    ...conversionsAttrs,
    ...personalAttributes.slice(sourceIdx),
  ];

  const [filteredVariables, setFilteredVariables] = useState(
    personalAttributesWithCrms,
  );

  const categories = [
    {
      name: 'consumer',
      icon: (
        <IdcardOutlined className="contact-attributes__category-row-icon" />
      ),
    },
    {
      name: 'engagement',
      icon: <ClickedIcon className="contact-attributes__engagement-icon" />,
    },
    {
      name: 'conversions',
      icon: <FilterOutlined className="contact-attributes__engagement-icon" />,
    },
    {
      name: 'other',
      icon: (
        <AppstoreAddOutlined className="contact-attributes__category-row-icon" />
      ),
    },
  ];

  const attributesBusyName = 'attributes';

  const [searchText, setSearchText] = useState('');
  const [newAttributeNameText, setNewAttributeNameText] = useState('');
  const [newAttributeValueText, setNewAttributeValueText] = useState('');
  const [editAttributeName, setEditAttributeName] = useState('');
  const [showAddAttribute, setShowAddAttribute] = useState(false);

  const { update: updateContact } = useContactsCRUDActions();
  const { deleteAttributeValue, addAttribute } = useAttributeCRUDActions();

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const channel = identifyChannel(remoteUserId);
  const allowSystemVariableEditing = channel !== 'facebook';
  const { openGeneralConfirmModal } = useModalActions();

  const isDummyContact = selectedContact?.dummy;

  const categorizedVariables = new Set(
    Object.values(variablesByCategory).flat(),
  );
  const otherVariables = filteredVariables.filter(
    v => !categorizedVariables.has(v.variable.friendlyName || ''),
  );

  useEffect(() => {
    updateFilteredVariables(searchText);
  }, [personalAttributesWithCrms.length]);

  useEffect(() => {
    setDefaults();
    setSearchText('');
    updateFilteredVariables('');
  }, [contactId]);

  const updateFilteredVariables = (newSearchText: string) => {
    if (!newSearchText) {
      // setFilteredVariables(variablesExcludingHidden);

      setFilteredVariables(personalAttributesWithCrms);

      return;
    }
    const lowerSearchText = newSearchText.toLowerCase().trim();
    setFilteredVariables(
      personalAttributesWithCrms.filter(
        v =>
          (v.variable?.friendlyName?.toLowerCase()?.indexOf(lowerSearchText) ||
            0) >= 0,
      ),
    );
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText =
      e.target.value.length === 1 ? e.target.value.trim() : e.target.value;
    setSearchText(newSearchText);
    updateFilteredVariables(newSearchText);
  };

  const onAttributeAdd = (attributeName: string, attributeValue: string) => {
    if (contactId) {
      return addAttribute(contactId, attributeName, attributeValue);
    }
  };

  const createAttribute = async (e: React.FormEvent<HTMLDivElement>) => {
    e.preventDefault();
    const attributeNameText = newAttributeNameText.trim();
    const attributeValueText = newAttributeValueText.trim();
    if (attributeNameText && attributeValueText) {
      if (EDITABLE_SYSTEM_VARS.includes(editAttributeName)) {
        if (contactId) {
          await updateContact(facebookPageId, {
            id: contactId,
            [camelCase(editAttributeName)]: attributeValueText,
          });
        }
        onAttributeUpdated?.(attributesBusyName);
        setDefaults();
        return;
      }
      const newAttribute = await onAttributeAdd(
        attributeNameText,
        attributeValueText,
      );
      if (newAttribute) {
        onAttributeUpdated?.(attributesBusyName);
        setDefaults();
      }
    }
  };

  const handleSetNewAttributeNameText = (e: ChangeEvent<HTMLInputElement>) =>
    setNewAttributeNameText(e.target.value);
  const handleSetNewAttributeValueText = (e: ChangeEvent<HTMLInputElement>) =>
    setNewAttributeValueText(e.target.value);

  const onClickEdit = (attribute: ContactVariable) => {
    setEditAttributeName(attribute.variable.name);
    setNewAttributeValueText(attribute.value);
    setNewAttributeNameText(attribute.variable.name);
  };

  const setDefaults = () => {
    setEditAttributeName('');
    setNewAttributeNameText('');
    setNewAttributeValueText('');
  };

  const cancelAttributeEditing = () => {
    setDefaults();
  };

  const attributesWrapperClasses = classNames('contact-attributes__container', {
    'live-chat--contact-attributes__container': isLiveChatContacts,
  });

  const onRemoveAttribute = (item: ContactVariable) => {
    openGeneralConfirmModal({
      headerText: 'Remove attribute',
      messageText: `Are you sure you want to remove this attribute from this contact?`,
      okButtonText: 'Remove',
      cancelButtonFirst: true,
      onCloseHandler: async ok => {
        if (ok) {
          await deleteAttributeValue(item.id);
          onAttributeUpdated?.(attributesBusyName);
        }
      },
    });
  };

  const setUnsubscribeTrue = async () => {
    if (contactId) {
      await onAttributeAdd('UNSUBSCRIBED', 'true');
      onAttributeUpdated?.(attributesBusyName);
    }
  };

  const getMenu: (attribute: ContactVariable) => INodeMenuItem[] = attribute =>
    [
      attribute.variable.name === 'UNSUBSCRIBED' && {
        disabled: false,
        children: 'Set to true',
        onClick: setUnsubscribeTrue,
      },
      {
        disabled: false,
        children: 'Edit',
        onClick: () => onClickEdit(attribute),
      },
      !EDITABLE_SYSTEM_VARS.includes(attribute.variable.name) && {
        disabled: false,
        children: 'Remove',
        onClick: () => onRemoveAttribute(attribute),
      },
    ].filter(Boolean) as INodeMenuItem[];

  if (collapsedContactView) {
    return (
      <div className="live-chat--contact__header--collapsed">
        <AppstoreFilled className="contact-details__header-icon" />
        <span className="live-chat--contact__content-number--collapsed">
          ({filteredVariables?.length})
        </span>
      </div>
    );
  }

  return (
    <div className={attributesWrapperClasses}>
      <AccordionItem
        icon={<AppstoreFilled className="contact-details__header-icon" />}
        title="Attributes"
        count={updatedVariables.filter(v => v.value).length}
        closeOnOutsideClick={false}
        openOnMount={!isBusinessContact}
        withHeaderButton={withHeaderButton}
        withHeaderButtonText={withHeaderButtonText}
        className="contact-details__accordion-item"
        headingClassName="contact-details__accordion-item-heading"
        headerButtonCss={
          headerButtonCss || {
            container: 'contact-attributes__header-button',
          }
        }
        openArrowClassName={openArrowClassName}
        arrowClassName="contact-details__caret-arrow"
        onHeaderButtonClick={onHeaderButtonClick}
        caretStyleIcon
      >
        <div className="contact-attributes__main-content">
          <div className="contact-attributes__header-wrapper">
            <div
              className={classNames('contact-attributes__header', {
                'contact-attributes__header-active': showAddAttribute,
              })}
              onClick={() => {
                if (isDummyContact) {
                  return;
                }
                setShowAddAttribute(!showAddAttribute);
              }}
            >
              <PlusOutlined />
              Add Attribute
            </div>

            <SearchInput
              placeholder="Search Attribute"
              className="contact-attributes__search"
              value={searchText}
              onChange={onChange}
              useDarkStyle
            />
          </div>

          {showAddAttribute && (
            <div className="contact-attributes__add-attribute-container">
              <form>
                <div className="live-chat--contact-attributes-input-container">
                  <input
                    value={newAttributeNameText}
                    placeholder="Name"
                    onChange={handleSetNewAttributeNameText}
                  />
                  <input
                    value={newAttributeValueText}
                    placeholder="Value"
                    onChange={handleSetNewAttributeValueText}
                  />
                  <div
                    className={classNames(
                      'contact-details-header__profile-name-input-button',
                      {
                        'contact-details-header__profile-name-input-button-disabled':
                          newAttributeNameText.trim().length === 0 ||
                          newAttributeValueText.trim().length === 0,
                      },
                    )}
                    onClick={e => createAttribute(e)}
                  >
                    <CheckOutlined className="contact-details-header__profile-name-input-button-check-mark" />
                  </div>
                </div>
              </form>
            </div>
          )}

          <div className="contact-attributes__attributes">
            <BusinessAttributes
              variables={updatedVariables}
              isBusinessContact={isBusinessContact}
            />
            {categories.map(category => {
              const filteredByCategory = (
                category.name === 'other'
                  ? otherVariables
                  : filteredVariables.filter(v =>
                      variablesByCategory[category.name]?.includes(
                        v.variable.friendlyName,
                      ),
                    )
              ).filter(v => v.value);

              return filteredByCategory?.length ? (
                <AccordionItem
                  key={category.name}
                  count={filteredByCategory?.length}
                  arrowDirectionVertical
                  closeOnOutsideClick={false}
                  className="contact-attributes__category-accordion"
                  headerWrapperClassName="contact-attributes__category-accordion-header"
                  titleComponent={
                    <div className="contact-attributes__category-row">
                      {category.icon} {category.name}
                    </div>
                  }
                >
                  {filteredByCategory?.map((variable: ContactVariable, idx) => {
                    const unsbuscribedEditable =
                      variable.variable.name === 'UNSUBSCRIBED'
                        ? (channel === 'sms' || channel === 'email') &&
                          variable.value === 'false'
                        : true;
                    const notEditable =
                      !(
                        allowSystemVariableEditing &&
                        EDITABLE_SYSTEM_VARS.includes(variable.variable.name)
                      ) &&
                      (variable.variable.system ||
                        !variable.id ||
                        !unsbuscribedEditable);
                    return !notEditable &&
                      editAttributeName === variable.variable.name ? (
                      <form key={`${variable.id}-${idx}`}>
                        <div className="contact-attributes__editable-attribute-row">
                          <div className="live-chat--contact-attributes-input-container">
                            <input
                              value={newAttributeNameText}
                              placeholder="Name"
                              onChange={handleSetNewAttributeNameText}
                              disabled
                            />
                            <input
                              value={newAttributeValueText}
                              placeholder="Value"
                              onChange={handleSetNewAttributeValueText}
                            />

                            <div className="live-chat--contact-attributes-buttons live-chat--contact-attributes-buttons_edit">
                              <div
                                className={classNames(
                                  'contact-details-header__profile-name-input-button',
                                  {
                                    'contact-details-header__profile-name-input-button-disabled':
                                      newAttributeNameText.trim().length ===
                                        0 ||
                                      newAttributeValueText.trim().length === 0,
                                  },
                                )}
                                onClick={e => createAttribute(e)}
                              >
                                <CheckOutlined className="contact-details-header__profile-name-input-button-check-mark" />
                              </div>
                              <CloseOutlined
                                className="contact-details-header__cancel-btn"
                                onClick={cancelAttributeEditing}
                              />
                            </div>
                          </div>
                        </div>
                      </form>
                    ) : (
                      !isNil(variable.value) && (
                        <div
                          key={`${variable.id}-${idx}`}
                          className="contact-attributes__attribute-row"
                        >
                          <div className="contact-attributes__attribute-name">
                            {variable.variable.friendlyName}
                          </div>
                          <div className="contact-attributes__attribute-value-container">
                            <div className="contact-attributes__attribute-value">
                              {processAttributes(
                                variable.value,
                                variable.variable.name,
                              )}
                            </div>
                            {!notEditable &&
                              getMenu(variable).length &&
                              !isDummyContact && (
                                <Menu
                                  menu={getMenu(variable)}
                                  direction="bottom"
                                  useThreeDotButton
                                />
                              )}
                          </div>
                        </div>
                      )
                    );
                  })}
                </AccordionItem>
              ) : null;
            })}
          </div>
        </div>
      </AccordionItem>
    </div>
  );
};
